/**
 * Share (FR-29, FR-30, D26, D27) — one dialog for **a document, a selection
 * or a folder**, replacing the prototype's share menu:
 *
 * - **People** — members of the organisation, picked from its directory (the
 *   host reads core-org-management's members; ProDoc keeps none). A member
 *   share widens their *sight*; what they may do stays their ProDoc role.
 * - **Link** — for someone outside the platform: who may open it (people of
 *   the organisation, signed in — or **anyone holding it**, which needs
 *   step-up), how long it works (1–30 days), and a passcode. The token is
 *   **shown once**, to copy, and dropped when the dialog closes; only its hash
 *   is kept by the service. A folder link is live: what is filed there later is
 *   in it, a document moved out leaves it (§9-18).
 * - **Send** — a link just made, by e-mail or WhatsApp **through comm as the
 *   organisation** (its own connected channel when it has one, FiveOX's in its
 *   name otherwise). The passcode never travels in the message.
 * - **App** — a sibling app (an administrator's, `settings:can_update`), which
 *   is what lets it read a document it did not bring (FR-32).
 *
 * And the shares that already reach it, each with *Revoke* for its creator or
 * a holder of `share:can_delete`.
 *
 * Package rules (ADR-0010): the host hands in its client, the organisation's
 * member list and its step-up; nothing here knows a session or a router.
 */
import { Alert, Badge, Button, Dialog, EmptyState, Input, MultiSelect, RadioGroup, Select, Skeleton, SkeletonRegion, Tabs, Textarea, formatDate, formatDateTime } from '@fiveox/ui'
import { Check, Copy, Link2, Mail, MessageCircle } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import {
  MAX_LINK_DAYS,
  MAX_SEND_MESSAGE,
  MAX_SEND_RECIPIENTS,
  MAX_SHARE_DOCUMENTS,
  MAX_SHARE_FOLDERS,
  MAX_SHARE_MEMBERS,
  MIN_LINK_DAYS,
  PASSCODE_MAX,
  PASSCODE_MIN,
  SHARE,
  isStepUpCancelled,
  mayRevokeShare,
  mayShare,
  mayShareToApp,
  shareIsLive,
  sharingOf,
  withStepUp,
  type ChannelState,
  type CreatedShare,
  type ProDocClient,
  type Share,
  type ShareAccess,
  type ShareChannel,
  type ShareInput,
  type StepUpAsk,
  type Verb,
} from './prodoc-client'
import { plural } from './bits'
import { errorText, useLoad } from './hooks'

/** One member of the organisation, as the host's directory names them. */
export interface ShareMember {
  user_id: string
  name: string | null
  email: string | null
}

/** What the host lends the sharing screens: the organisation's members, its step-up, and where a link opens. */
export interface SharingHostProps {
  /** core-org-management's members, read by the host with its own session. Without it, no member share is offered. */
  listMembers?: () => Promise<ShareMember[]>
  /** The host's step-up (T6.25) — an `anyone` link needs it. */
  requestStepUp?: StepUpAsk
  /** Where `/s/<token>` opens when the service did not address the link itself — ProDoc's web origin. */
  shareLinkBase?: string
}

/** What is being shared. */
export interface ShareTarget {
  documents: { id: string; title: string }[]
  folders: { id: string; name: string }[]
}

export interface ShareDialogProps extends SharingHostProps {
  client: ProDocClient
  open: boolean
  target: ShareTarget
  can: (resource: string, verb: Verb) => boolean
  userId?: string | null
  onClose: () => void
  /** After a share is made or revoked — the host may re-read a count. */
  onChanged?: () => void
}

type Mode = 'people' | 'link' | 'app' | 'shares'

const DAYS = [1, 3, 7, 14, 30]
const CHANNEL_LABEL: Record<ShareChannel, string> = { email: 'E-mail', whatsapp: 'WhatsApp' }

/** "Share “Invoice 7”", "Share the folder “Returns”", "Share 3 documents". */
export function shareTitle(target: ShareTarget): string {
  const { documents: d, folders: f } = target
  if (d.length === 1 && f.length === 0) return `Share “${d[0]!.title}”`
  if (f.length === 1 && d.length === 0) return `Share the folder “${f[0]!.name}”`
  return `Share ${[d.length ? plural(d.length, 'document') : '', f.length ? plural(f.length, 'folder') : ''].filter(Boolean).join(' and ')}`
}

/** What a message says it carries — comm's `shared_what` reads the same way. */
const what = (target: ShareTarget) =>
  target.folders.length && !target.documents.length
    ? target.folders.length === 1
      ? 'a folder'
      : plural(target.folders.length, 'folder')
    : plural(target.documents.length, 'document')

export function ShareDialog(props: ShareDialogProps) {
  const { client, open, target, can, userId, onClose, onChanged, listMembers, requestStepUp, shareLinkBase } = props
  const canCreate = mayShare(can)
  const single = target.documents.length + target.folders.length === 1
  const listable = single && can(SHARE, 'can_view')
  const [mode, setMode] = useState<Mode>(canCreate ? 'link' : 'shares')
  const [chosen, setChosen] = useState<string[]>([])
  const [access, setAccess] = useState<ShareAccess>('org')
  const [days, setDays] = useState<string>('')
  const [passcode, setPasscode] = useState('')
  const [app, setApp] = useState('')
  // The token lives here, on screen, and nowhere else — dropped when the dialog closes.
  const [created, setCreated] = useState<CreatedShare | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [hidden, setHidden] = useState(false)
  const [revision, setRevision] = useState(0)

  const settings = useLoad(() => (open ? client.settings.get() : Promise.resolve(null)), [client, open])
  const sharing = sharingOf(settings.data)
  const members = useLoad(() => (open && listMembers ? listMembers() : Promise.resolve([] as ShareMember[])), [open, listMembers])
  const options = useLoad(() => (open && mayShareToApp(can) ? client.documents.options() : Promise.resolve(null)), [client, open])
  const shares = useLoad(() => {
    if (!open || !listable) return Promise.resolve([] as Share[])
    return target.documents.length === 1 ? client.documents.shares(target.documents[0]!.id) : client.shares.list({ folder_id: target.folders[0]!.id })
    // The target's identity is its ids.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, open, listable, revision, target.documents.map((d) => d.id).join(), target.folders.map((f) => f.id).join()])

  // Closing forgets everything typed — and the token above all (T6.27).
  useEffect(() => {
    if (open) return
    setMode(canCreate ? 'link' : 'shares')
    setChosen([])
    setAccess('org')
    setDays('')
    setPasscode('')
    setApp('')
    setCreated(null)
    setBusy(false)
    setError('')
    setNotice('')
    setHidden(false)
  }, [open, canCreate])

  const nameOf = useMemo(() => {
    const byId = new Map((members.data ?? []).map((m) => [m.user_id, m.name || m.email || null]))
    return (id: string | null) => (id ? (id === userId ? 'you' : (byId.get(id) ?? 'a member')) : 'someone')
  }, [members.data, userId])

  const tooMany = target.documents.length > MAX_SHARE_DOCUMENTS || target.folders.length > MAX_SHARE_FOLDERS
  const expiryDays = Number(days || sharing.link_expiry_days)
  // The service's bounds (`app/domain/sharing.py`): said here, before the request.
  const code = passcode.trim()
  const passcodeError =
    sharing.link_passcode_required && !code
      ? 'This organisation requires a passcode on every link.'
      : code && code.length < PASSCODE_MIN
        ? `At least ${PASSCODE_MIN} characters.`
        : null

  /** Make the share; an `anyone` link re-verifies first, with this dialog stepped aside while it does. */
  const make = async (input: ShareInput, onMade: (made: CreatedShare) => void) => {
    setBusy(true)
    setError('')
    setNotice('')
    try {
      const ask: StepUpAsk | undefined = requestStepUp
        ? async (request) => {
            setHidden(true)
            try {
              return await requestStepUp(request)
            } finally {
              setHidden(false)
            }
          }
        : undefined
      const made = await withStepUp(
        (assertion) => client.shares.create(input, { stepUpAssertion: assertion }),
        ask,
        { title: 'Share with anyone holding the link', what: `Anyone who has this link can open ${what(target)} until it expires — no FiveOX account needed.` },
        input.kind === 'link' && input.access === 'anyone',
      )
      onMade(made)
      setRevision((n) => n + 1)
      onChanged?.()
    } catch (e) {
      setError(isStepUpCancelled(e) ? 'Nothing was shared — the re-verification was cancelled.' : errorText(e))
    } finally {
      setBusy(false)
    }
  }

  const items = { document_ids: target.documents.map((d) => d.id), folder_ids: target.folders.map((f) => f.id) }

  const revoke = async (share: Share) => {
    setError('')
    try {
      await client.shares.revoke(share.id)
      setNotice(share.kind === 'link' ? 'Revoked — the link no longer opens.' : 'Revoked.')
      setRevision((n) => n + 1)
      onChanged?.()
    } catch (e) {
      setError(errorText(e))
    }
  }

  const tabs = [
    ...(canCreate ? [{ id: 'link', label: 'Link' }, ...(listMembers ? [{ id: 'people', label: 'People' }] : []), ...(mayShareToApp(can) ? [{ id: 'app', label: 'App' }] : [])] : []),
    ...(listable ? [{ id: 'shares', label: 'Shared with' }] : []),
  ]

  return (
    <Dialog open={open && !hidden} onClose={onClose} title={shareTitle(target)} width="lg">
      <div className="space-y-4">
        {tabs.length > 1 && <Tabs idBase="prodoc-share" tabs={tabs} active={mode} onChange={(id) => { setMode(id as Mode); setError(''); setNotice('') }} />}
        {error && <Alert tone="error">{error}</Alert>}
        {notice && <Alert tone="success">{notice}</Alert>}
        {tooMany && (
          <Alert tone="warning">
            One share reaches at most {MAX_SHARE_DOCUMENTS} documents and {MAX_SHARE_FOLDERS} folders. Select fewer, or share the folder that holds them.
          </Alert>
        )}

        {!canCreate && !listable && <EmptyState variant="outline" title="You can’t share here" description="Your ProDoc role in this organisation does not include sharing. An administrator can change it in the FiveOX console." />}

        {canCreate && mode === 'link' &&
          (created ? (
            <LinkMade client={client} created={created} target={target} shareLinkBase={shareLinkBase} passcodeSet={Boolean(passcode)} />
          ) : settings.loading && !settings.data ? (
            <SkeletonRegion label="Reading the sharing defaults" className="space-y-2">
              <Skeleton className="h-6 w-56" />
              <Skeleton className="h-9 w-full" />
            </SkeletonRegion>
          ) : (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault()
                if (busy || tooMany || passcodeError) return
                void make({ kind: 'link', ...items, access, expires_in_days: expiryDays, ...(code ? { passcode: code } : {}) }, setCreated)
              }}
            >
              <RadioGroup<ShareAccess>
                legend="Who can open it"
                value={access}
                onChange={setAccess}
                options={[
                  { value: 'org', label: 'People in this organisation', description: 'They sign in to FiveOX first, and see only what the link reaches.' },
                  { value: 'anyone', label: 'Anyone with the link', description: 'No sign-in — for an auditor or a supplier. You confirm it is you first.' },
                ]}
              />
              <div className="grid gap-3 sm:grid-cols-2">
                <Select label="Works for" value={String(expiryDays)} onChange={(e) => setDays(e.target.value)}>
                  {[...new Set([...DAYS, sharing.link_expiry_days])]
                    .filter((n) => n >= MIN_LINK_DAYS && n <= MAX_LINK_DAYS)
                    .sort((a, b) => a - b)
                    .map((n) => (
                      <option key={n} value={n}>
                        {plural(n, 'day')}
                        {n === sharing.link_expiry_days ? ' (default)' : ''}
                      </option>
                    ))}
                </Select>
                <Input
                  label={sharing.link_passcode_required ? 'Passcode' : 'Passcode (optional)'}
                  value={passcode}
                  autoComplete="off"
                  maxLength={PASSCODE_MAX}
                  onChange={(e) => setPasscode(e.target.value)}
                  hint={`${PASSCODE_MIN} to ${PASSCODE_MAX} characters. Tell it to them separately — it is never sent with the link.`}
                  error={passcodeError}
                />
              </div>
              <div className="flex justify-end">
                <Button type="submit" loading={busy} loadingText="Making the link…" disabled={tooMany || Boolean(passcodeError)}>
                  <Link2 size={15} aria-hidden />
                  Make the link
                </Button>
              </div>
            </form>
          ))}

        {canCreate && mode === 'people' && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault()
              if (busy || tooMany || chosen.length === 0 || chosen.length > MAX_SHARE_MEMBERS) return
              void make({ kind: 'member', ...items, recipient_user_ids: chosen }, (made) => {
                setChosen([])
                setNotice(`Shared with ${plural(made.shares.length || chosen.length, 'member')}. They find it under Shared with me.`)
              })
            }}
          >
            {members.loading && !members.data ? (
              <SkeletonRegion label="Reading the organisation’s members">
                <Skeleton className="h-9 w-full" />
              </SkeletonRegion>
            ) : members.error ? (
              <Alert tone="error">Could not read the organisation’s members: {members.error}</Alert>
            ) : (
              <MultiSelect
                label="Members"
                value={chosen}
                onChange={setChosen}
                allowUnlisted={false}
                placeholder="Find a member…"
                emptyMessage="No member matches."
                hint={`They see it beside their own workspaces — up to ${MAX_SHARE_MEMBERS} at a time. What they may do with it is still their ProDoc role.`}
                error={chosen.length > MAX_SHARE_MEMBERS ? `At most ${MAX_SHARE_MEMBERS} members in one share.` : null}
                options={(members.data ?? [])
                  .filter((m) => m.user_id !== userId)
                  .map((m) => ({ value: m.user_id, label: m.name || m.email || m.user_id, sublabel: m.name && m.email ? m.email : undefined }))}
              />
            )}
            <div className="flex justify-end">
              <Button type="submit" loading={busy} loadingText="Sharing…" disabled={tooMany || chosen.length === 0}>
                Share
              </Button>
            </div>
          </form>
        )}

        {canCreate && mode === 'app' && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault()
              if (busy || tooMany || !app) return
              void make({ kind: 'app', ...items, recipient_app: app }, () => {
                setNotice(`${app} may now read ${what(target)} through ProDoc’s API.`)
                setApp('')
              })
            }}
          >
            <Select label="App" value={app} onChange={(e) => setApp(e.target.value)} hint="A sibling app reads only its own documents, and what the organisation shares with it here.">
              <option value="">Choose an app…</option>
              {(options.data?.owner_apps ?? [])
                .filter((slug) => slug !== (options.data?.app ?? 'prodoc'))
                .map((slug) => (
                  <option key={slug} value={slug}>
                    {slug}
                  </option>
                ))}
            </Select>
            <div className="flex justify-end">
              <Button type="submit" loading={busy} loadingText="Sharing…" disabled={tooMany || !app}>
                Share with the app
              </Button>
            </div>
          </form>
        )}

        {mode === 'shares' && listable && <ShareList shares={shares} can={can} userId={userId} nameOf={nameOf} onRevoke={(s) => void revoke(s)} />}
      </div>
    </Dialog>
  )
}

/* ------------------------------------------------------------- link made */

/** The link, once: copy it, or send it through comm as the organisation. */
function LinkMade({ client, created, target, shareLinkBase, passcodeSet }: { client: ProDocClient; created: CreatedShare; target: ShareTarget; shareLinkBase?: string; passcodeSet: boolean }) {
  const share = created.shares[0]
  const url = created.url ?? (created.token ? `${(shareLinkBase ?? '').replace(/\/+$/, '')}/s/${created.token}` : null)
  const [copied, setCopied] = useState(false)
  const [channel, setChannel] = useState<ShareChannel | null>(null)
  const channels = useLoad(() => client.shares.channels(), [client])

  const copy = async () => {
    if (!url) return
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className="space-y-4">
      {url ? (
        <div className="space-y-2">
          <div className="flex items-end gap-2">
            <Input label="The link" readOnly value={url} onFocus={(e) => e.target.select()} className="font-mono text-xs" />
            <Button type="button" variant={copied ? 'outline' : 'primary'} onClick={() => void copy()}>
              {copied ? <Check size={15} aria-hidden /> : <Copy size={15} aria-hidden />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <p className="text-muted-foreground text-xs">
            Shown this once — ProDoc keeps no copy of it. {share?.access === 'anyone' ? 'Anyone holding it can open it' : 'People of the organisation open it after signing in'}
            {share?.expires_at ? `, until ${formatDate(share.expires_at)}` : ''}.{passcodeSet ? ' They also need the passcode you set.' : ''}
          </p>
        </div>
      ) : (
        <Alert tone="warning">The link was made, but the service did not return it. Revoke it from Shared with, and make another.</Alert>
      )}

      {share && url && created.token && (
        <div className="border-border space-y-3 border-t pt-4">
          <p className="text-foreground text-sm font-medium">Send it</p>
          <div className="flex flex-wrap gap-2">
            {(['email', 'whatsapp'] as ShareChannel[]).map((c) => {
              const state = channels.data?.find((x) => x.channel === c)
              const blocked = Boolean(state && !state.active && !state.sends_as)
              return (
                <Button key={c} type="button" variant={channel === c ? 'secondary' : 'outline'} size="sm" disabled={blocked} onClick={() => setChannel(c)} title={state ? channelHint(state) : undefined}>
                  {c === 'email' ? <Mail size={14} aria-hidden /> : <MessageCircle size={14} aria-hidden />}
                  {c === 'email' ? 'By e-mail' : 'By WhatsApp'}
                </Button>
              )
            })}
          </div>
          {channel && <SendForm key={channel} client={client} shareId={share.id} token={created.token} channel={channel} state={channels.data?.find((x) => x.channel === channel)} target={target} />}
        </div>
      )}
    </div>
  )
}

/** What a send would leave as, said before it is sent (D27). */
function channelHint(state: ChannelState): string {
  if (state.active || state.sends_as === 'org') return `From the organisation’s own ${state.channel === 'email' ? 'mail server' : 'WhatsApp number'}${state.provider ? ` (${state.provider})` : ''}.`
  if (state.sends_as === 'platform') return 'From FiveOX, in the organisation’s name.'
  return `Cannot be sent this way yet${state.reason ? ` (${state.reason.replace(/_/g, ' ')})` : ''}.`
}

/** A send needs the token on screen: the service rebuilds `/s/<token>` from it, sent as `X-Share-Token` (it keeps only the hash). */
function SendForm({ client, shareId, token, channel, state, target }: { client: ProDocClient; shareId: string; token: string; channel: ShareChannel; state: ChannelState | undefined; target: ShareTarget }) {
  const [to, setTo] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [sent, setSent] = useState<string | null>(null)
  const recipients = to
    .split(/[,;\n]/)
    .map((r) => r.trim())
    .filter(Boolean)
  const toError = recipients.length > MAX_SEND_RECIPIENTS ? `At most ${MAX_SEND_RECIPIENTS} at a time.` : null

  if (sent) {
    return (
      <Alert tone="success">
        Sent by {CHANNEL_LABEL[channel]} to {plural(recipients.length, channel === 'email' ? 'address' : 'number', channel === 'email' ? 'addresses' : 'numbers')} — {sent}
      </Alert>
    )
  }
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault()
        if (busy || recipients.length === 0 || toError) return
        setBusy(true)
        setError('')
        client.shares
          .send(shareId, { channel, recipients, message, token })
          .then((r) =>
            setSent(
              `${r.sent_as === 'org' ? 'from the organisation’s own channel' : 'from FiveOX, in the organisation’s name'}${r.failed ? ` (${r.failed} could not be reached)` : ''}.`,
            ),
          )
          .catch((err) => setError(errorText(err)))
          .finally(() => setBusy(false))
      }}
    >
      {error && <Alert tone="error">{error}</Alert>}
      <Input
        label="To"
        value={to}
        onChange={(e) => setTo(e.target.value)}
        inputMode={channel === 'email' ? 'email' : 'tel'}
        placeholder={channel === 'email' ? 'name@company.com, …' : '98765 43210, …'}
        hint={channel === 'email' ? 'Separate addresses with commas — up to 10.' : 'Mobile numbers, separated by commas — up to 10.'}
        error={toError}
      />
      <Textarea label="Message (optional)" rows={2} maxLength={MAX_SEND_MESSAGE} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="For the September close." />
      <p className="text-muted-foreground text-xs">
        {state ? channelHint(state) : 'Sent through FiveOX.'} It carries the link to {what(target)}, never the files, and never the passcode.
      </p>
      <div className="flex justify-end">
        <Button type="submit" size="sm" loading={busy} loadingText="Sending…" disabled={recipients.length === 0 || Boolean(toError)}>
          Send
        </Button>
      </div>
    </form>
  )
}

/* ------------------------------------------------------------ the shares */

function ShareList({
  shares,
  can,
  userId,
  nameOf,
  onRevoke,
}: {
  shares: { data: Share[] | null; loading: boolean; error: string; reload: () => Promise<void> }
  can: (resource: string, verb: Verb) => boolean
  userId?: string | null
  nameOf: (id: string | null) => string
  onRevoke: (share: Share) => void
}) {
  if (shares.loading && !shares.data) {
    return (
      <SkeletonRegion label="Reading the shares" className="space-y-2">
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-2/3" />
      </SkeletonRegion>
    )
  }
  if (shares.error && !shares.data) {
    return (
      <Alert tone="error">
        Could not read the shares: {shares.error}{' '}
        <Button variant="link" onClick={() => void shares.reload()}>
          Try again
        </Button>
      </Alert>
    )
  }
  const list = shares.data ?? []
  if (list.length === 0) return <EmptyState variant="outline" title="Not shared yet" description="Nobody outside its workspace sees it, and no link opens it." />
  return (
    <ul className="divide-border divide-y" aria-label="Shares">
      {list.map((s) => {
        const live = shareIsLive(s)
        return (
          <li key={s.id} className="flex flex-wrap items-start justify-between gap-3 py-2.5">
            <div className="min-w-0">
              <p className="text-foreground text-sm font-medium">
                {s.kind === 'member' ? `With ${nameOf(s.recipient_user_id)}` : s.kind === 'app' ? `With the app ${s.recipient_app}` : s.access === 'anyone' ? 'A link — anyone holding it' : 'A link — people of the organisation'}
              </p>
              <p className="text-muted-foreground text-xs">
                {[
                  s.created_at ? `by ${nameOf(s.created_by)}, ${formatDateTime(s.created_at)}` : `by ${nameOf(s.created_by)}`,
                  s.expires_at ? `${live ? 'until' : 'ended'} ${formatDate(s.expires_at)}` : null,
                  s.has_passcode ? 'passcode' : null,
                  s.via_folder_id ? 'through its folder' : null,
                  s.sent_channel ? `sent by ${s.sent_channel}${s.sent_as ? ` as ${s.sent_as === 'org' ? 'the organisation' : 'FiveOX'}` : ''}` : null,
                  s.last_opened_at ? `last opened ${formatDateTime(s.last_opened_at)}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {!live && <Badge size="sm">{s.revoked_at ? 'Revoked' : 'Expired'}</Badge>}
              {live && mayRevokeShare(can, s, userId) && (
                <Button variant="outline" size="sm" onClick={() => onRevoke(s)}>
                  Revoke
                </Button>
              )}
            </div>
          </li>
        )
      })}
    </ul>
  )
}
